import Dexie from 'dexie'
import { emitter } from './emitter'
import { newGuid } from '../utils'

Dexie.delete('main')
const _DBVersion_ = '1.1.0'
const db = new Dexie('main')
let _callback = null

  ; (async db => {
    db.version(1).stores({
      global: '++id, name, version, metadata, mqtt, upload',
      device: 'id, type',
      plcDevice: 'id, name, manufacturer, connectionType, interfaceType, interface, args, module',
      plcData: 'id, plcDeviceId, type, address',
      func: 'id, parentId, parentType, type, args, order',
      interface: 'id, name, type, args',
      analogDevice: 'id, deviceId, name, interfaceType, interface, module',
      modbusDevice: 'id, deviceId, name,protocolType, interfaceType, interface, module, address, port, unitId',
      modbusData: 'id, modbusDeviceId, cmdType, address',
      modbusDataPoint: 'id, modbusDataId, offset, regNum',
      metadata: 'id, dId, name, raw, points',
      uploadDevice: 'id, dId, dName, interval',
      uploadData: 'id, uploadDeviceId, deviceId, dataId, iName, iId, iUnit, iCode',
      dlt645Device: 'id, deviceId, name, interfaceType, interface, module, address',
      dlt645Data: 'id, dlt645DeviceId, dataId'
    })

    try {
      await db.open()
    } catch (err) {
      alert(err)
    }

    await Promise.all(db.tables.map(table => table.clear()))

    await db.global.add({
      name: '',
      version: _DBVersion_,
      metadata: {},
      mqtt: {},
      upload: {
        type: '',
        module: '',
        args: {}
      }
    })

    if (_callback) {
      _callback()
    }
  })(db)

export const DB = db
export const DBVersion = _DBVersion_

async function mountInfo () {
  const info = Object.create(null)

  for (let deviceClass of ['plcDevice', 'analogDevice', 'modbusDevice', 'dlt645Device']) {
    for (let device of await db[deviceClass].toArray()) {
      info[device.interface] = info[device.interface] || []

      info[device.interface].push({
        type: deviceClass,
        data: device
      })
    }
  }

  return info
}

async function generateDeviceConfig (obj) {
  const device = obj.data
  const res = {}

  /* id */
  res.id = device.deviceId

  /* name */
  res.name = device.name

  /* module */
  res.module = device.module

  /* module args */
  res.moduleArgs = ({
    plcDevice () {
      return device.args
    },
    analogDevice () {
      return {}
    },
    modbusDevice () {
      if (device.module === 'dpe-modbus-tcp') {
        return {
          host: device.address,
          port: device.port,
          unitId: device.unitId
        }
      }
      return {
        address: device.address,
        mode: device.protocolType === 'RTU' ? 'rtu' : 'ascii'
      }
    },
    dlt645Device () {
      return {
        address: device.address
      }
    }
  })[obj.type]()

  /* data */
  const sortFunc = (a, b) => a.order - b.order
  res.data = await ({
    async plcDevice () {
      const plcDataList = await db.plcData.where({ plcDeviceId: device.id }).toArray()

      return Promise.all(plcDataList.map(async plcData => {
        const funcList = await db.func.where({
          parentId: plcData.id,
          parentType: 'plcData'
        }).toArray()

        return {
          id: plcData.id,
          address: plcData.address,
          type: plcData.type,
          function: await funcList.sort(sortFunc).map(func => [func.type].concat(func.args))
        }
      }))
    },
    async analogDevice () {
      const funcList = await db.func.where({
        parentId: device.id,
        parentType: 'analogDevice'
      }).toArray()

      return [{
        id: device.deviceId,
        function: await funcList.sort(sortFunc).map(func => [func.type].concat(func.args))
      }]
    },
    async modbusDevice () {
      const list = await db.modbusData.where({ modbusDeviceId: device.id }).toArray()

      return Promise.all(list.map(async modbusData => {
        const points = await db.modbusDataPoint.where({modbusDataId: modbusData.id}).toArray()
        const pointPromise = points.map(async point => {
          const funcList = await db.func.where({
            parentId: point.id,
            parentType: 'modbusDataPoint'
          }).toArray()
          return {
            offset: point.offset,
            regNum: point.regNum,
            id: point.id,
            function: funcList.sort(sortFunc).map(func => [func.type].concat(func.args))
          }
        })

        return {
          id: modbusData.id,
          cmdType: modbusData.cmdType,
          address: modbusData.address,
          regNum: modbusData.regNum,
          points: await Promise.all(pointPromise)
        }
      }))
    },
    async dlt645Device () {
      const list = await db.dlt645Data.where({ dlt645DeviceId: device.id }).toArray()

      return Promise.all(list.map(async dlt645Data => {
        const funcList = await db.func.where({
          parentId: dlt645Data.id,
          parentType: 'dlt645Data'
        }).toArray()

        return {
          id: dlt645Data.id,
          dataId: dlt645Data.dataId,
          function: await funcList.sort(sortFunc).map(func => [func.type].concat(func.args))
        }
      }))
    }
  })[obj.type]()
  return res
}

async function generateConfigWithoutMetadata () {
  const interfaces = {}
  const info = await mountInfo()
  const basicConfig = await db.global.get(1)

  for (let iface of await db.interface.toArray()) {
    /* initialize */
    interfaces[iface.type] = interfaces[iface.type] || []

    let obj = {
      config: {},
      devices: []
    }
    /* config */
    obj.config = iface.args

    /* devices */
    obj.devices = !info[iface.id] ? [] : await Promise.all(info[iface.id].map(async obj => generateDeviceConfig(obj)))
    interfaces[iface.type].push(obj)
  }

  /* workaround: extend net array */
  /*
  if (interfaces.NET && interfaces.NET.length >= 1) {
    var newInterfaceNet = [
      {
        'config': interfaces.NET[0].config,
        'devices': []
      }
    ]

    for (let i = 0; i < interfaces.NET.length; i++) {
      for (let device of interfaces.NET[i].devices) {
        newInterfaceNet[0].devices.push(device)
      }
    }

    interfaces.NET = newInterfaceNet
  }
  */

  return {
    basic: {
      name: basicConfig.name,
      version: basicConfig.version
    },
    interface: interfaces
  }
}

async function verifyConfig (config) {
  /* verify upload data */
  const datas = await db.uploadData.toArray()

  for (let data of datas) {
    let device = await db.device.get(data.deviceId)
    if (device) {
      let deviceData
      switch (device.type) {
        case 'plc':
          deviceData = await db.plcData.get(data.dataId)
          break
        case 'modbus':
          deviceData = await db.modbusDataPoint.get(data.dataId)
          break
        case 'analog':
          deviceData = device
          break
        case 'dlt645':
          deviceData = await db.dlt645Data.get(data.dataId)
          break
        default:
          throw new Error(`Unknow device type: '${device.type}'`)
      }

      if (deviceData) {
        continue
      }
    }

    throw new Error(`上报数据 '${data.iName}' 没有关联设备`)
  }
}

async function generateConfig () {
  await verifyConfig()

  let config = await generateConfigWithoutMetadata()

  let res = Object.assign(config, {
    metadata: await genMetadata(),
    du: await genDataupload()
  })

  return res
}

async function genDataupload () {
  try {
    const upload = (await db.global.get(1)).upload

    if (upload.module === 'data-upload-http') {
      upload.args.uri = `http://${upload.args.uri}`
    }

    return {
      module: upload.module,
      moduleArgs: upload.args
    }
  } catch (err) {
    throw new Error('未设置数据上报信息')
  }
}

async function genMetadata () {
  const data = await Promise.all((await db.uploadDevice.toArray()).map(async device => {
    const iDatas = await (await db.uploadData.where({ uploadDeviceId: device.id })).toArray()

    return {
      dId: device.dId,
      dName: device.dName,
      interval: parseInt(parseFloat(device.interval) * 1000),
      iData: iDatas.map(iData => {
        if (!iData.deviceId || !iData.dataId) {
          throw new Error(`${device.dName}:${iData.iName} does not assigned!`)
        }

        return {
          _id: {
            device: iData.deviceId,
            data: iData.dataId
          },
          iId: iData.iId,
          iCode: iData.iCode || undefined,
          iName: iData.iName,
          iUnit: iData.iUnit
        }
      })
    }
  }))

  return Object.assign({ data }, (await db.global.get(1)).metadata)
}

/*
async function genMetadataLegacy () {
  const metadata = (await db.global.get(1)).metadata

  await Promise.all(metadata.data.map(async data => {
    const device = (await db.metadata.where({ dId: data.dId }).toArray())[0]
    return Promise.all(data.iData.map(async (iData, index) => {
      const point = device.points[index]
      if (point.deviceId === 0) {
        throw new Error(`'${device.name}/${point.name}' does not assigned`)
      }

      iData._id = {
        device: point.deviceId,
        data: point.dataId
      }
    }))
  }))

  return metadata
}
*/

async function _storeUploadDevice (device) {
  return db.uploadDevice.add({
    id: newGuid(),
    dId: device.dId,
    dName: device.dName,
    interval: device.interval
  })
}

async function _storeUploadData (deviceId, data) {
  return db.uploadData.add({
    id: newGuid(),
    uploadDeviceId: deviceId,
    deviceId: data.deviceId,
    dataId: data.dataId,
    iName: data.iName,
    iId: data.iId,
    iUnit: data.iUnit,
    iCode: data.iCode
  })
}

async function _storeGlobalMetadata (data) {
  return db.global.update(1, {
    metadata: {
      gSN: data.metadata.gSN,
      gId: data.metadata.gId,
      cId: data.metadata.cId
    }
  })
}

async function loadMetadata (data) {
  await _storeGlobalMetadata(data)

  const newMetadata = await _generateNewMetadata(data)
  await db.uploadDevice.clear()
  await db.uploadData.clear()

  for (let device of newMetadata) {
    let id = await _storeUploadDevice(device)
    for (let iData of device.points) {
      await _storeUploadData(id, iData)
    }
  }

  emitter.emit('updateMetadata')
}

async function _generateNewMetadata (data) {
  return Promise.all(data.metadata.data.map(async device => {
    const oldDevice = await (await db.uploadDevice.where({ dId: device.dId })).toArray()
    const oldDeviceId = oldDevice.length === 1 ? oldDevice[0].id : null

    return {
      dId: device.dId,
      dName: device.dName,
      interval: (device.interval / 1000).toString(),
      points: await Promise.all(device.iData.map(async (iData, index) => {
        let deviceId = 0
        let dataId = 0

        if (oldDeviceId !== null) {
          const oldPoints = await (await db.uploadData.where({
            uploadDeviceId: oldDeviceId,
            iId: iData.iId
          })).toArray()
          const oldPoint = oldPoints.length === 1 ? oldPoints[0] : null

          if (oldPoint) {
            deviceId = oldPoint.deviceId
            dataId = oldPoint.dataId
          }
        }

        return {
          deviceId: deviceId,
          dataId: dataId,
          iName: iData.iName,
          iId: iData.iId,
          iUnit: iData.iUnit,
          iCode: iData.iCode
        }
      }))
    }
  }))
}

async function dump () {
  const data = await Promise.all(db.tables.map(async table => {
    return {
      name: table.name,
      schemaSyntax: (() => {
        let primKeyAndIndexes = [table.schema.primKey].concat(table.schema.indexes)
        let schemaSyntax = primKeyAndIndexes.map(index => index.src).join(',')

        return schemaSyntax
      })(),
      datas: await db[table.name].toArray()
    }
  }))

  return data
}

async function load (data) {
  if (isValid(data)) {
    await clear()
    redefineStores(data)
    await importData(data)
    emitter.emit('imported')
  } else {
    throw new Error('导入失败,工程文件版本过旧')
  }
}

function isValid (data) {
  return _DBVersion_ === data.find(item => item.name === 'global').datas[0].version
}

async function addDefaultInterface (interfaceTree) {
  await db.interface.clear()
  let dataRows = Object.keys(interfaceTree).reduce((prev, typeName) => {
    let res = interfaceTree[typeName].map(item => {
      const { name } = item
      delete item.name
      return { id: newGuid(), name, type: typeName, args: item }
    })
    return prev.concat(res)
  }, [])
  await db.interface.bulkAdd(dataRows)
}

async function clear () {
  return Promise.all(db.tables.map(table => table.clear()))
}

function redefineStores (data) {
  db.close()

  const stores = data.reduce((accumulator, currentValue) => {
    accumulator[currentValue.name] = currentValue.schemaSyntax

    return accumulator
  }, {})

  db.version(1).stores(stores)

  return db.open()
}

async function importData (data) {
  return Promise.all(data.map(info => db[info.name].bulkAdd(info.datas)))
}

async function loadConnection (data) {
  let type = ({
    'data-upload-mqtt': 'MQTT',
    'data-upload-http': 'HTTP',
    'data-upload-tcp': 'TCP',
    'data-upload-azure': 'AZURE',
    'data-upload-aws': 'AWS'
  })[data.du.module]

  const upload = {
    type,
    module: data.du.module,
    args: data.du.moduleArgs
  }

  await db.global.update(1, { upload })

  emitter.emit('updateMetadata')
}

async function setSerialNumber (serialNumber) {
  let data = await db.global.get(1)

  Object.assign(data.metadata, { gSN: serialNumber })

  return db.global.update(1, data)
}

function then (callback) {
  _callback = callback
}

async function reset () {
  await Promise.all(db.tables
    .filter(table => table.name !== 'global')
    .map(table => table.clear()))

  await db.global.update(1, {
    name: '',
    version: _DBVersion_,
    metadata: {},
    mqtt: {},
    upload: {
      type: '',
      module: '',
      args: {}
    }
  })

  emitter.emit('imported')
}

export default {
  install (Vue, options) {
    Vue.mixin({
      beforeCreate () {
        this.$db = db
      }
    })
  },
  generateConfig,
  generateConfigWithoutMetadata,
  loadMetadata,
  loadConnection,
  dump,
  load,
  isValid,
  addDefaultInterface,
  genDataupload,
  setSerialNumber,
  then,
  reset
}



// WEBPACK FOOTER //
// ./src/extensions/db.js